import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FileText, Image, File, Folder, Upload } from 'lucide-react';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getAllFilesAsync, deleteFileAsync, updateFileAsync } from '@/data/store/actions';
import { getAllFiles, getFileSubfolders } from '@/data/store/selectors';
import { RequestParams, TAbac, TFileItem, TFileSubfoldersItem } from '@/data/types';
import {
  FilesHeader,
  FilesControls,
  FilesList,
  BreadcrumbNavigation,
  FileUploadModal,
  CreateFolderModal,
  ExternalUrlModal,
  FilePaths,
} from './components';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import PopupTransition from '@/components/ui/PopupTransition';
import { AbacMain } from '../Test/Abac';

const Files: React.FC = () => {
  const dispatch = useAppDispatch();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('/'); // Start from root path
  const [localFolders, setLocalFolders] = useState<TFileSubfoldersItem[]>([]); // Local folders before sync
  const [isDragOver, setIsDragOver] = useState(false); // Drag and drop state
  const [dragDropFiles, setDragDropFiles] = useState<File[]>([]); // Files from drag and drop
  const [showDragDropUpload, setShowDragDropUpload] = useState(false); // Show drag and drop upload modal
  const [infiniteScrollKey, setInfiniteScrollKey] = useState(0); // Key to trigger infinite scroll refresh
  const [fileToDelete, setFileToDelete] = useState<TFileItem | null>(null); // File to delete
  const [isDeleting, setIsDeleting] = useState(false); // Deletion loading state

  const [abacFile, setAbacFile] = useState<TFileItem | null>(null);

  // Modal states for action buttons
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [isExternalUrlModalOpen, setIsExternalUrlModalOpen] = useState(false);

  // Move file modal states
  const [isMoveFileModalOpen, setIsMoveFileModalOpen] = useState(false);
  const [fileToMove, setFileToMove] = useState<TFileItem | null>(null);

  // Redux selectors
  const filesData = useSelector(getAllFiles);
  const subfoldersData = useSelector(getFileSubfolders);

  // API action wrapper
  const attemptGetAllFiles = useCallback(
    async (params: RequestParams) => {
      const searchParams = searchTerm.trim() ? { ...params, search: searchTerm.trim() } : params;
      return await dispatch(getAllFilesAsync(searchParams));
    },
    [dispatch, searchTerm],
  );

  // Helper functions - memoized to prevent re-renders
  const getFileIcon = useCallback((file: TFileItem) => {
    const type = file.content_type || file.filetype || '';

    if (type.includes('pdf')) {
      return <FileText className="h-8 w-8 text-red-500" />;
    } else if (type.includes('word') || type.includes('document')) {
      return <FileText className="h-8 w-8 text-blue-500" />;
    } else if (type.includes('excel') || type.includes('spreadsheet')) {
      return <FileText className="h-8 w-8 text-green-500" />;
    } else if (type.includes('image')) {
      return <Image className="h-8 w-8 text-purple-500" />;
    } else if (!type && !file.content_type) {
      return <Folder className="h-8 w-8 text-yellow-500" />;
    } else {
      return <File className="h-8 w-8 text-gray-500" />;
    }
  }, []);

  const formatFileSize = useCallback((bytes: number | null | undefined): string => {
    if (!bytes) return 'Unknown size';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  }, []);

  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString('el-GR');
  }, []);

  const getFileCategory = useCallback((file: TFileItem): string => {
    const type = file.content_type || file.filetype || '';

    if (type.includes('pdf')) return 'Documents';
    if (type.includes('image')) return 'Images';
    if (type.includes('video')) return 'Videos';
    if (type.includes('audio')) return 'Audio';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'Spreadsheets';
    if (type.includes('word') || type.includes('document')) return 'Documents';
    if (type.includes('zip') || type.includes('rar')) return 'Archives';

    return 'Files';
  }, []);

  const canDownloadFile = useCallback((file: TFileItem): boolean => {
    // Can only download files with protected_url (actual uploaded files)
    // External URLs should not have download option
    return !!file.protected_url;
  }, []);

  const handlePreview = useCallback((file: TFileItem) => {
    if (file.protected_url) {
      window.open(file.protected_url, '_blank', 'noopener,noreferrer');
    } else if (file.external_url) {
      window.open(file.external_url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('No preview URL available for file:', file.name);
    }
  }, []);

  const handleDownload = useCallback(async (file: TFileItem) => {
    // Don't allow download for external URLs - they should only be previewed
    if (file.external_url && !file.protected_url) {
      console.warn('Download not available for external URLs:', file.name);
      return;
    }

    const downloadUrl = file.protected_url;

    if (!downloadUrl) {
      console.warn('No download URL available for file:', file.name);
      return;
    }

    try {
      // For protected URLs, fetch the data and trigger download
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob data
      const blob = await response.blob();

      // Create object URL for the blob
      const objectUrl = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = file.name;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: try direct link approach
      try {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = file.name;
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
        // Last resort: open in new tab
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }, []);

  // Combine backend subfolders with local folders
  const allSubfolders = useMemo(() => {
    const combined = [...(subfoldersData || []), ...localFolders];
    // Remove duplicates based on directory path
    const uniqueSubfolders = combined.filter(
      (item, index, self) => index === self.findIndex((t) => t.directory === item.directory),
    );
    return uniqueSubfolders;
  }, [subfoldersData, localFolders]);

  // Navigation handlers
  const handleNavigateToPath = useCallback((path: string) => {
    setCurrentPath(path);
    setOpenDropdown(null); // Close any open dropdowns when navigating
  }, []);

  const handleNavigateToParent = useCallback(() => {
    if (currentPath === '/') return; // Already at root

    const pathSegments = currentPath.split('/').filter((segment) => segment);
    const parentPath = pathSegments.length === 1 ? '/' : '/' + pathSegments.slice(0, -1).join('/');

    setCurrentPath(parentPath);
    setOpenDropdown(null);
  }, [currentPath]);

  // Folder creation handler
  const handleCreateFolder = useCallback(
    (folderName: string) => {
      if (!folderName.trim()) return;

      // Create the full path for the new folder
      const newFolderPath = currentPath === '/' ? `/${folderName.trim()}` : `${currentPath}/${folderName.trim()}`;

      // Add to local folders with count 0 (no files yet)
      const newLocalFolder: TFileSubfoldersItem = {
        directory: newFolderPath,
        count: 0,
      };

      setLocalFolders((prev) => [...prev, newLocalFolder]);
    },
    [currentPath],
  );

  // File upload success handler
  const handleUploadSuccess = useCallback(() => {
    // Refresh the current folder's files and subfolders by making a new request
    // The InfiniteScroll component will automatically refresh when we trigger this
    const params = searchTerm.trim() ? { query: searchTerm.trim(), path: currentPath } : { path: currentPath };

    dispatch(getAllFilesAsync(params));
  }, [dispatch, searchTerm]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only activate drag over if files are being dragged
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only deactivate if leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setDragDropFiles(files);
      setShowDragDropUpload(true);
    }
  }, []);

  const handleCloseDragDropUpload = useCallback(() => {
    setDragDropFiles([]);
    setShowDragDropUpload(false);
  }, []);

  // File deletion handlers
  const handleDeleteFile = useCallback((file: TFileItem) => {
    setFileToDelete(file);
    setOpenDropdown(null); // Close dropdown when opening delete modal
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!fileToDelete) return;

    setIsDeleting(true);
    try {
      await dispatch(
        deleteFileAsync({
          fileId: fileToDelete.id,
          params: {},
        }),
      );

      // Refresh the infinite scroll by incrementing the key
      setInfiniteScrollKey((prev) => prev + 1);

      // Close the modal
      setFileToDelete(null);
    } catch (error) {
      console.error('Failed to delete file:', error);
      // Could add error notification here
    } finally {
      setIsDeleting(false);
    }
  }, [dispatch, fileToDelete]);

  const handleCancelDelete = useCallback(() => {
    if (!isDeleting) {
      setFileToDelete(null);
    }
  }, [isDeleting]);

  // File move handlers
  const handleMoveFile = useCallback((file: TFileItem) => {
    setFileToMove(file);
    setIsMoveFileModalOpen(true);
    setOpenDropdown(null); // Close dropdown when opening move modal
  }, []);

  const handleConfirmMove = useCallback(
    (newPath: string) => {
      if (!fileToMove) return;

      // TODO: Implement actual move file API call
      dispatch(updateFileAsync({ fileId: fileToMove.id, payload: { path: newPath } }));
      console.log('Moving file:', fileToMove.name, 'to path:', newPath);

      // For now, just close the modal and refresh
      setIsMoveFileModalOpen(false);
      setFileToMove(null);

      // Refresh the infinite scroll by incrementing the key
      setInfiniteScrollKey((prev) => prev + 1);
    },
    [fileToMove],
  );

  const handleCancelMove = useCallback(() => {
    setIsMoveFileModalOpen(false);
    setFileToMove(null);
  }, []);

  // Action button handlers
  const handleCreateFolderClick = useCallback(() => {
    setIsCreateFolderModalOpen(true);
  }, []);

  const handleUploadFileClick = useCallback(() => {
    setIsFileUploadModalOpen(true);
  }, []);

  const handleAddExternalUrlClick = useCallback(() => {
    setIsExternalUrlModalOpen(true);
  }, []);

  const handleCreateFolderFromModal = useCallback(
    (folderName: string) => {
      handleCreateFolder(folderName);
      setIsCreateFolderModalOpen(false);
    },
    [handleCreateFolder],
  );

  // Event handlers

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleViewModeChange = useCallback((mode: 'grid' | 'list') => {
    setViewMode(mode);
  }, []);

  const handleDropdownToggle = useCallback(
    (fileId: string) => {
      setOpenDropdown(openDropdown === fileId ? null : fileId);
    },
    [openDropdown],
  );

  const handleUpdateAbac = async (abac: TAbac[]) => {
    if (abacFile) {
      await dispatch(updateFileAsync({ fileId: abacFile.id, payload: { abac: abac } }));
      setInfiniteScrollKey((prev) => prev + 1);
      setAbacFile(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside dropdown elements
      if (!target.closest('[data-dropdown]') && !target.closest('[data-dropdown-trigger]')) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdown]);

  return (
    <>
      <div
        className="relative min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 p-6"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDragOver && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-blue-900/20 backdrop-blur-sm">
            <div className="rounded-xl border-2 border-dashed border-blue-400 bg-white/90 p-12 text-center shadow-xl">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-blue-100 p-4">
                <Upload className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">Αποθέστε αρχεία εδώ</h3>
              <p className="text-gray-600">Θα μεταφορτωθούν στο: {currentPath === '/' ? 'Root' : currentPath}</p>
            </div>
          </div>
        )}

        <div className="relative z-10 mx-auto max-w-7xl">
          <FilesHeader />

          <FilesControls
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onCreateFolder={handleCreateFolderClick}
            onUploadFile={handleUploadFileClick}
            onAddExternalUrl={handleAddExternalUrlClick}
          />

          <BreadcrumbNavigation
            onMove={handleMoveFile}
            filesData={filesData}
            currentPath={currentPath}
            onNavigateToPath={handleNavigateToPath}
          />

          <FilesList
            attemptGetAllFiles={attemptGetAllFiles}
            filesData={filesData}
            subfoldersData={allSubfolders}
            viewMode={viewMode}
            searchTerm={searchTerm}
            openDropdown={openDropdown}
            onDropdownToggle={handleDropdownToggle}
            getFileIcon={getFileIcon}
            setAbacFile={setAbacFile}
            formatFileSize={formatFileSize}
            formatDate={formatDate}
            getFileCategory={getFileCategory}
            canDownloadFile={canDownloadFile}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onMove={handleMoveFile}
            onDelete={handleDeleteFile}
            currentPath={currentPath}
            onNavigateToPath={handleNavigateToPath}
            onCreateFolder={handleCreateFolder}
            onUploadSuccess={handleUploadSuccess}
            infiniteScrollKey={infiniteScrollKey}
          />

          {/* Drag and Drop Upload Modal */}
          <FileUploadModal
            isOpen={showDragDropUpload}
            onClose={handleCloseDragDropUpload}
            currentPath={currentPath}
            onUploadSuccess={() => {
              handleUploadSuccess();
              handleCloseDragDropUpload();
            }}
            initialFiles={dragDropFiles}
          />

          {/* Action Button Modals */}
          <CreateFolderModal
            isOpen={isCreateFolderModalOpen}
            onClose={() => setIsCreateFolderModalOpen(false)}
            onCreateFolder={handleCreateFolderFromModal}
            currentPath={currentPath}
          />

          <FileUploadModal
            isOpen={isFileUploadModalOpen}
            onClose={() => setIsFileUploadModalOpen(false)}
            currentPath={currentPath}
            onUploadSuccess={handleUploadSuccess}
          />

          <ExternalUrlModal
            isOpen={isExternalUrlModalOpen}
            onClose={() => setIsExternalUrlModalOpen(false)}
            currentPath={currentPath}
            onUploadSuccess={handleUploadSuccess}
          />

          {/* File Deletion Confirmation Modal */}
          <ConfirmationModal
            isOpen={!!fileToDelete}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            title="Διαγραφή αρχείου"
            message={`Είστε σίγουροι ότι θέλετε να διαγράψετε το αρχείο "${fileToDelete?.name}"? Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.`}
            confirmText="Διαγραφή"
            cancelText="Ακύρωση"
            variant="danger"
            isLoading={isDeleting}
          />

          {/* File Move Modal */}
          <PopupTransition
            open={isMoveFileModalOpen}
            onClose={handleCancelMove}
            closeOnEscape={true}
            closeOnDocumentClick={true}
          >
            {fileToMove && (
              <FilePaths
                currentPath={currentPath}
                initialPath={fileToMove.path || '/'}
                onSelectPath={handleConfirmMove}
                onCancel={handleCancelMove}
                fileName={fileToMove.name}
              />
            )}
          </PopupTransition>
          <PopupTransition open={!!abacFile} onClose={() => setAbacFile(null)}>
            <AbacMain initialAbac={abacFile?.abac} onSave={handleUpdateAbac} />
          </PopupTransition>
        </div>
      </div>
    </>
  );
};

export default Files;
